/**
 * The notification settings form's model: what the form holds, what it sends. Credentials are write-only on the server
 * (`PATCH /notifications/config` keeps a credential the update leaves out, and every channel it leaves out).
 */
import { z } from 'zod';
import type { NotificationConfig, NotificationConfigUpdate } from '../../generated/api/types.gen.js';
import { zNotificationConfigUpdate } from '../../generated/api/zod.gen.js';

// The form holds every channel it shows (Slack has no section). The email recipients are typed as one comma-separated
// line; the API takes a list. Webhook headers are not edited here, so the server keeps the stored ones.
const { shape } = zNotificationConfigUpdate;
export const notificationsSchema = zNotificationConfigUpdate.omit({ slack: true }).extend({
  telegram: shape.telegram.unwrap(),
  discord: shape.discord.unwrap(),
  email: shape.email.unwrap().omit({ recipients: true }).extend({ recipients: z.string() }),
  webhook: shape.webhook.unwrap().omit({ headers: true }),
});
export type NotificationsValues = z.output<typeof notificationsSchema>;

/** Credentials are write-only: the read shows only their last 4 characters, so each field starts unset. */
export function toFormValues(config: NotificationConfig): NotificationsValues {
  return {
    telegram: {
      enabled: config.telegram?.enabled ?? false,
      botToken: undefined,
      chatId: config.telegram?.chatId ?? '',
    },
    discord: { enabled: config.discord?.enabled ?? false, webhookUrl: undefined },
    email: {
      enabled: config.email?.enabled ?? false,
      host: config.email?.host ?? '',
      port: config.email?.port ?? 587,
      secure: config.email?.secure ?? false,
      user: config.email?.user ?? '',
      pass: undefined,
      from: config.email?.from ?? '',
      recipients: (config.email?.recipients ?? []).join(', '),
    },
    webhook: { enabled: config.webhook?.enabled ?? false, url: undefined },
  };
}

export type ChannelKey = keyof NotificationsValues;

/** Only the channels the person changed: the server keeps every channel the update leaves out, as it was. */
export function toRequest(values: NotificationsValues, changed: ReadonlySet<ChannelKey>): NotificationConfigUpdate {
  const { email } = values;
  return {
    ...(changed.has('telegram') ? { telegram: values.telegram } : {}),
    ...(changed.has('discord') ? { discord: values.discord } : {}),
    ...(changed.has('email') && email
      ? {
          email: {
            ...email,
            recipients: email.recipients
              .split(',')
              .map((recipient) => recipient.trim())
              .filter((recipient) => recipient !== ''),
          },
        }
      : {}),
    ...(changed.has('webhook') ? { webhook: values.webhook } : {}),
  };
}

/** The channels whose fields differ from what the form started with. */
export function changedChannels(values: NotificationsValues, baseline: NotificationsValues): Set<ChannelKey> {
  const channels = Object.keys(values) as ChannelKey[];
  return new Set(
    channels.filter((channel) => {
      const now: Record<string, unknown> = values[channel] ?? {};
      const before: Record<string, unknown> = baseline[channel] ?? {};
      return [...new Set([...Object.keys(now), ...Object.keys(before)])].some((field) => now[field] !== before[field]);
    }),
  );
}

/** "Saved, ends in 1234. Leave blank to keep it." — or nothing when no credential is saved. */
export function savedHint(masked: string | undefined): string | undefined {
  return masked ? `Saved, ends in ${masked.slice(-4)}. Leave blank to keep it.` : undefined;
}
